import { Col, Row } from 'react-bootstrap';
import moment from 'moment';
import { toJS } from 'mobx';

import { inject, observer } from 'mobx-react';
import DataTable from '../core/components/datatable/DataTable';
import { useComponentDidMount } from '../core/hooks';
import {
  clientActivityActions,
  ClientActivityStore,
  ClientActivityDevice,
  ClientActivityEvent,
  DEFAULT_ACTIVITY_PAGE_SIZE,
} from './store/activityStore';
import { EVENT_TYPE_TITLES, formatEventTime } from './activityAggregations';
import ClientActivityDashboards from './ClientActivityDashboards';

const joinDefined = (parts: Array<string | undefined>): string => (
  parts.filter(Boolean).join(' ')
);

const joinWithComma = (parts: Array<string | undefined>): string => (
  parts.filter(Boolean).join(', ')
);

const deviceColumns = [
  { title: 'Платформа', name: 'platform', width: 110 },
  { title: 'Устройство', name: 'deviceModel', width: 160 },
  {
    title: 'ОС',
    name: 'osName',
    width: 140,
    getCellValue: (row: ClientActivityDevice) => joinDefined([row?.osName, row?.osVersion]),
  },
  {
    title: 'Браузер',
    name: 'browserName',
    width: 150,
    getCellValue: (row: ClientActivityDevice) => joinDefined([row?.browserName, row?.browserVersion]),
  },
  {
    title: 'Версия приложения',
    name: 'appVersion',
    width: 140,
    getCellValue: (row: ClientActivityDevice) => joinDefined([row?.appVersion, row?.appBuild]),
  },
  { title: 'IP-адрес', name: 'lastIpAddress', width: 130 },
  {
    title: 'Город',
    name: 'city',
    width: 150,
    getCellValue: (row: ClientActivityDevice) => joinWithComma([row?.country, row?.region, row?.city]),
  },
  {
    title: 'Впервые замечено',
    name: 'firstSeenAt',
    width: 170,
    getCellValue: (row: ClientActivityDevice) => formatEventTime(row?.firstSeenAt),
  },
  {
    title: 'Последняя активность',
    name: 'lastSeenAt',
    width: 170,
    getCellValue: (row: ClientActivityDevice) => formatEventTime(row?.lastSeenAt),
  },
  { title: 'Запросов', name: 'seenCount', width: 110, align: 'right' },
];

const eventColumns = [
  {
    title: 'Дата и время',
    name: 'eventTime',
    width: 170,
    getCellValue: (row: ClientActivityEvent) => formatEventTime(row?.eventTime),
    // отформатированная строка сортируется лексикографически неверно — конвертируем в timestamp
    sortValueConverter: (value: string) => (value ? moment(value, 'DD.MM.YYYY HH:mm:ss').valueOf() : 0),
  },
  {
    title: 'Событие',
    name: 'eventType',
    width: 160,
    getCellValue: (row: ClientActivityEvent) => EVENT_TYPE_TITLES[row?.eventType] ?? row?.eventType,
  },
  { title: 'Платформа', name: 'platform', width: 110 },
  { title: 'Устройство', name: 'deviceModel', width: 150 },
  {
    title: 'ОС',
    name: 'osName',
    width: 140,
    getCellValue: (row: ClientActivityEvent) => joinDefined([row?.osName, row?.osVersion]),
  },
  {
    title: 'Браузер',
    name: 'browserName',
    width: 150,
    getCellValue: (row: ClientActivityEvent) => joinDefined([row?.browserName, row?.browserVersion]),
  },
  { title: 'Версия приложения', name: 'appVersion', width: 140 },
  { title: 'IP-адрес', name: 'ipAddress', width: 130 },
  {
    title: 'Город',
    name: 'city',
    width: 150,
    getCellValue: (row: ClientActivityEvent) => joinWithComma([row?.country, row?.region, row?.city]),
  },
  {
    title: 'Источник',
    name: 'sourceDomain',
    width: 160,
    getCellValue: (row: ClientActivityEvent) => joinDefined([row?.sourceDomain, row?.widget ? '(виджет)' : undefined]),
  },
  {
    title: 'UTM',
    name: 'utmSource',
    width: 180,
    getCellValue: (row: ClientActivityEvent) => [row?.utmSource, row?.utmMedium, row?.utmCampaign]
      .filter(Boolean)
      .join(' / '),
  },
  {
    title: 'URI запроса',
    name: 'requestUri',
    width: 300,
    getCellValue: (row: ClientActivityEvent) => row?.requestUri ?? '',
  },
];

interface ClientActivityProps {
  clientId: string,
  clientActivityStore?: Pick<ClientActivityStore,
  'activity' | 'activityByClientId' | 'eventsSample' | 'eventsSampleByClientId' | 'loading'>,
}

const ClientActivity = (props: ClientActivityProps) => {
  const {
    clientActivityStore: store,
    clientId,
  } = props;

  const {
    activity, activityByClientId, eventsSample, eventsSampleByClientId, loading,
  } = store;

  // В data DevExpress отдаём plain-массивы (toJS): observable-массив внутри грида
  // ловит MobX-ловушки на splice/map (тот же урок, что с recharts).
  const devices = toJS(activity?.devices) || [];
  const events = toJS(activity?.events) || [];
  const sample = toJS(eventsSample) || [];

  const loadData = async (page = 0, size = DEFAULT_ACTIVITY_PAGE_SIZE) => {
    await clientActivityActions.getClientActivity(clientId, page, size);
  };

  useComponentDidMount(() => {
    if (activityByClientId !== clientId) {
      loadData();
    }
    // Выборка для дашбордов — один раз на клиента, отдельно от пагинации таблицы
    if (eventsSampleByClientId !== clientId) {
      clientActivityActions.getEventsSample(clientId);
    }
  });

  // DataTable серверной пагинации отдаёт offset (page * size)
  const handleCurrentPageChange = (offset: number) => {
    const size = activity?.size ?? DEFAULT_ACTIVITY_PAGE_SIZE;
    loadData(offset / size, size);
  };

  const handlePageSizeChange = (size: number) => {
    loadData(0, size);
  };

  return (
    <Col md={ 12 }>
      <ClientActivityDashboards activity={ activity } eventsSample={ sample } />
      <Row>
        <Col md={ 12 }>
          <h4 className='mb-3'>Устройства</h4>
          <DataTable
            loading={ loading }
            data={ devices }
            columns={ deviceColumns }
            hideToolbar
            hidePagination
            noDataText='Устройства не найдены'
          />
        </Col>
      </Row>
      <Row className='mt-4'>
        <Col md={ 12 }>
          <h4 className='mb-3'>История событий</h4>
          <DataTable
            loading={ loading }
            data={ events }
            columns={ eventColumns }
            dataLength={ activity?.total }
            currentPage={ activity?.page ?? 0 }
            pageSize={ activity?.size ?? DEFAULT_ACTIVITY_PAGE_SIZE }
            localSorting
            hideToolbar
            onCurrentPageChange={ handleCurrentPageChange }
            onPageSizeChange={ handlePageSizeChange }
          />
        </Col>
      </Row>
    </Col>
  );
};

export default inject('clientActivityStore')(observer(ClientActivity));
